import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { useEffect } from 'react'

// Components
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { AuthGuard } from '@/components/layout/AuthGuard'
import { AppLayout } from '@/components/layout/AppLayout'

// Pages
import { LoginPage } from '@/pages/LoginPage'
import { CallbackPage } from '@/pages/CallbackPage'
import { DashboardPage } from '@/pages/DashboardPage'
import { ClientsPage } from '@/pages/ClientsPage'
import { ClientDetailPage } from '@/pages/ClientDetailPage'
import { ProgramsPage } from '@/pages/ProgramsPage'
import { LibraryPage } from '@/pages/LibraryPage'
import { SchedulePage } from '@/pages/SchedulePage'
import { ProfilePage } from '@/pages/ProfilePage'
import { SettingsPage } from '@/pages/SettingsPage'
import { NotFoundPage } from '@/pages/NotFoundPage'

// Hooks
import { useCoachStore } from '@/stores/coachStore'

// API Services
import { apiService } from '@/services/api'
import { enhancedApiService } from '@/services/enhancedApi'

function App() {
  const { isLoading, isAuthenticated, user, getAccessTokenSilently } = useAuth0()
  const { setCoach, setAccessToken } = useCoachStore()

  // Initialize coach data and API service when authenticated
  useEffect(() => {
    const initializeCoach = async () => {
      if (isAuthenticated && user) {
        try {
          // Set up API services with Auth0 token getter
          apiService.setTokenGetter(getAccessTokenSilently)
          enhancedApiService.setTokenGetter(getAccessTokenSilently)
          
          const token = await getAccessTokenSilently()
          setAccessToken(token)
          
          // Set basic coach info from Auth0 user
          setCoach({
            id: user.sub || '',
            email: user.email || '',
            name: user.name || '',
            profilePhoto: user.picture,
            specializations: [],
            certifications: [],
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            status: 'active',
            createdAt: new Date(),
            updatedAt: new Date(),
          })
        } catch (error) {
          console.error('Failed to initialize coach:', error)
        }
      }
    }

    initializeCoach()
  }, [isAuthenticated, user, getAccessTokenSilently, setCoach, setAccessToken])

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center space-y-4">
          <LoadingSpinner size="lg" />
          <p className="text-gray-600">Loading Fit Coach...</p>
        </div>
      </div>
    )
  }

  return (
    <Routes>
      {/* Auth0 callback route - must be outside AuthGuard */}
      <Route path="/callback" element={<CallbackPage />} />
      
      {/* All other routes */}
      <Route path="/*" element={
        !isAuthenticated ? (
          <LoginPage />
        ) : (
          <AuthGuard>
            <AppLayout>
              <Routes>
                {/* Dashboard - Default route */}
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<DashboardPage />} />
                
                {/* Client Management */}
                <Route path="/clients" element={<ClientsPage />} />
                <Route path="/clients/:clientId" element={<ClientDetailPage />} />
                
                {/* Program Management */}
                <Route path="/programs" element={<ProgramsPage />} />
                <Route path="/programs/create" element={<ProgramsPage />} />
                <Route path="/programs/assign-days" element={<ProgramsPage />} />
                <Route path="/programs/:programId" element={<ProgramsPage />} />
                <Route path="/programs/:programId/edit" element={<ProgramsPage />} />
                
                {/* Library */}
                <Route path="/library" element={<LibraryPage />} />
                
                {/* Schedule */}
                <Route path="/schedule" element={<SchedulePage />} />

                {/* Profile and Settings */}
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/settings" element={<SettingsPage />} />
                
                {/* 404 */}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </AppLayout>
          </AuthGuard>
        )
      } />
    </Routes>
  )
}

export default App